import axios from 'axios';
import {useIntl} from 'react-intl';
import {useState, useRef, useCallback, useMemo, useEffect} from 'react';

import URL_CONST from '../../../../config/url';
import {IJobs} from '../../contexts/JobsDto';
import {getHeaders} from '../../../../store/dataService';
import {toast} from '../../../../base-components/StudioToast';

type VideoBinaryPreview = {
  url?: string;
  jobId?: string;
  previewIsAlreadyGenerated?: boolean;
};

type VideosBinariesPreviewState = Record<string, VideoBinaryPreview>;

const TIME_BEFORE_RETRY_PREVIEW_GENERATION = 3_000;

interface UseVideoPreviewProps {
  datasetId?: string;
  jobs?: IJobs;
}

export const useVideoPreview = ({datasetId, jobs}: UseVideoPreviewProps) => {
  const intl = useIntl();
  const isMountedRef = useRef(true);
  const [videosBinariesPreview, setVideosBinariesPreview] = useState<
    VideosBinariesPreviewState
  >({});
  const pendingBinaryIdsRef = useRef<{[binaryId: string]: boolean}>({});

  useEffect(function updateReferenceWhenComponentUnmounted() {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isPreviewAlreadyGenerated = useCallback(async url => {
    let isGenerated = true;

    try {
      const headers = await getHeaders({
        'Request-Timeout': 0,
      });

      await axios.get(url, {
        headers,
      });
    } catch (error) {
      isGenerated = false;
    }

    return isGenerated;
  }, []);

  const generateVideoPreviewJob = useCallback(
    async (binaryId: string, url: string) => {
      try {
        const queryString = url.split('?')[1] || '';
        const urlParams = new URLSearchParams(queryString);
        const dimensions = urlParams.get('previewDimensions');
        const [width, height] = dimensions ? dimensions.split('x').map(Number) : [];

        if (width && height && datasetId) {
          const headers = await getHeaders({
            'Request-Timeout': 0,
          });

          const response = await axios.put(
            URL_CONST.DSS_DATASET_JOB,
            {
              type: 'PREVIEW',
              binaryId,
              animated: true,
              previewSizes: [{w: width, h: height}],
            },
            {
              params: {datasetId},
              headers,
            }
          );

          if (isMountedRef.current) {
            setVideosBinariesPreview(prev => ({
              ...prev,
              [binaryId]: {...(prev[binaryId] ?? {}), jobId: response.data.jobId, url},
            }));
          }
        }
      } catch (error) {
        toast.error(intl.formatMessage({id: 'dataPrep.generatingVideoPreview.error'}));
      }
    },
    [datasetId, intl]
  );

  const generateVideoPreview = useCallback(
    async (binaryId: string, url: string | null) => {
      if (
        url &&
        !videosBinariesPreview[binaryId] &&
        !pendingBinaryIdsRef.current[binaryId]
      ) {
        pendingBinaryIdsRef.current[binaryId] = true;

        if (isMountedRef.current) {
          setVideosBinariesPreview(prev => ({
            ...prev,
            [binaryId]: {},
          }));
        }

        if (await isPreviewAlreadyGenerated(url)) {
          if (isMountedRef.current) {
            setVideosBinariesPreview(prev => ({
              ...prev,
              [binaryId]: {
                ...(prev[binaryId] ?? {}),
                previewIsAlreadyGenerated: true,
              },
            }));
          }
        } else {
          await generateVideoPreviewJob(binaryId, url);
        }
      }
    },
    [generateVideoPreviewJob, isPreviewAlreadyGenerated, videosBinariesPreview]
  );

  const validateAndReGenereateVideoPreviewIfNeeded = useCallback(async () => {
    const promises = [];
    const binaryIds = Object.keys(videosBinariesPreview).filter(
      binaryId => !videosBinariesPreview[binaryId].previewIsAlreadyGenerated
    );

    for (const binaryId of binaryIds) {
      const video = videosBinariesPreview[binaryId];

      if (video.jobId && video.url) {
        const job = jobs?.getFinishedJobs().getJobById(video.jobId);

        if (job) {
          promises.push(async () => {
            let isPreviewReady = await isPreviewAlreadyGenerated(video.url);

            if (!isPreviewReady) {
              await new Promise(resolve => {
                setTimeout(resolve, TIME_BEFORE_RETRY_PREVIEW_GENERATION);
              });

              isPreviewReady = await isPreviewAlreadyGenerated(video.url);

              if (!isPreviewReady) {
                jobs?.removeJobById(video.jobId as string);
                await generateVideoPreviewJob(binaryId, video.url as string);
              }
            }

            if (isPreviewReady && isMountedRef.current) {
              setVideosBinariesPreview(prev => ({
                ...prev,
                [binaryId]: {
                  ...(prev[binaryId] ?? {}),
                  previewIsAlreadyGenerated: true,
                },
              }));
            }
          });
        }
      }
    }

    await Promise.all(promises.map(promise => promise()));
  }, [jobs, generateVideoPreviewJob, isPreviewAlreadyGenerated, videosBinariesPreview]);

  const [latestJobs, setLatestJobs] = useState(jobs);
  useEffect(
    function validateAndReGenereateVideoPreviewWhenJobsChange() {
      if (isMountedRef.current && JSON.stringify(latestJobs) !== JSON.stringify(jobs)) {
        setLatestJobs(jobs);
        validateAndReGenereateVideoPreviewIfNeeded();
      }
    },
    [jobs, latestJobs, validateAndReGenereateVideoPreviewIfNeeded]
  );

  const processedPreviewsVideosBinariesIds = useMemo(
    () =>
      Object.entries(videosBinariesPreview)
        .filter(([_, video]) => video.previewIsAlreadyGenerated)
        .map(([id]) => id),
    [videosBinariesPreview]
  );

  return {
    videosBinariesPreview,
    generateVideoPreview,
    processedPreviewsVideosBinariesIds,
  };
};
